'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { getConsent, setConsent } from '@/lib/consent';

// Bottom-anchored cookie banner. Shows once until the user picks Accept
// or Reject. Choice persists for one year via lib/consent. The "manage
// cookies" footer link on /privacy can re-open it (event-driven, see
// useEffect's listener for the np:consent-open event).

const REOPEN_EVENT = 'np:consent-open';

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to next tick so SSR/CSR mismatch can't flash an unwanted banner
    const t = setTimeout(() => {
      if (getConsent() === null) setVisible(true);
    }, 250);

    function reopen() { setVisible(true); }
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => {
      clearTimeout(t);
      window.removeEventListener(REOPEN_EVENT, reopen);
    };
  }, []);

  function decide(choice: 'accepted' | 'essential') {
    setConsent(choice);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-modal="false"
      className="fixed inset-x-0 bottom-0 z-[100] bg-ink text-bone shadow-2xl border-t border-bone/10"
    >
      <div className="max-w-[1100px] mx-auto px-6 py-5 lg:px-12 lg:py-6 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-10">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.14em] text-gold mb-2 font-medium">
            Cookies
          </p>
          <p className="text-sm leading-relaxed text-bone/85">
            We use cookies to keep your cart working, remember your preferences,
            and understand how the site is used.{' '}
            <Link href="/privacy" className="underline hover:text-bone transition-colors">
              Read our privacy policy
            </Link>{' '}
            for the full list.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <button
            type="button"
            onClick={() => decide('essential')}
            className="border border-bone/35 text-bone px-6 py-3 text-xs uppercase tracking-[0.12em] font-medium hover:border-bone hover:bg-bone/5 transition-colors rounded-full"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => decide('accepted')}
            className="bg-bone text-ink px-6 py-3 text-xs uppercase tracking-[0.12em] font-medium hover:bg-bone/90 transition-colors rounded-full"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
